import { RecordEventTypeEnum } from 'components/framework/Enums';
import {
  Requirement,
  User,
  type WorkflowStatus,
} from 'components/framework/models';
import {
  type RecordEventRaw,
  type WorkUnitEnum_value,
  type RecordEventTypeEnum_value,
} from 'components/framework/models/generated/types';
import Debug from 'debug';
import { isPresent } from 'javascripts/util/isPresent';
import { orZero } from 'javascripts/util/orZero';
import { cloneDeep, last, merge } from 'lodash';
import moment from 'moment';
import UserPerformance from './UserPerformance';
import TeamPerformance from './TeamPerformance';
import { objectWithDefault } from 'javascripts/util/objectWithDefault';
import { inCurrentUserTimezone } from 'javascripts/dateUtils';
const debug = Debug('retrospective_report:parseRecordData');

export type ParsedRecordData = {
  originalEstimate?: number;
  remainingEstimate?: number;
  workDone?: number;
  committed?: boolean;
  committedEstimate?: number;
  committedUserId?: RecordId;
  started: boolean;
  completed?: boolean;
  removed?: boolean;
  event?: RecordEventRaw;
  id: RecordId;
  referenceNum: string;
  assignedToUser?: {
    id: RecordId;
    name: string;
    initials: string;
    avatarUrl: string | null;
  };
  teamWorkflowStatus?: WorkflowStatus;
  addedOutsideIteration?: boolean;
  addedOn?: string;
  removedOn?: string;
};

export type RollupData = {
  originalEstimate: number;
  remainingEstimate: number;
  workDone: number;
  rollup: boolean;
  rolledUpFrom: Set<string>;
};

/**
 * A bucket of data for a time period. This is used to build up the data for
 * parsed events.
 */
export class ParsedIterationRecordEventsTimeBucket {
  start: Date;
  end: Date;
  events: RecordEventRaw[] = [];
  records: { [recordId: RecordId]: ParsedRecordData };
  teamPerformance: TeamPerformance;
  teamUsers: Record<TeamUserId, UserPerformance>;
  rollups: { [recordId: RecordId]: RollupData };

  constructor(start: Date) {
    this.start = start;
    this.teamPerformance = new TeamPerformance();
    this.teamUsers = objectWithDefault<UserPerformance, TeamUserId>(
      () => new UserPerformance(new User())
    );
  }

  /**
   * Create a copy of the data that does not reference the original data so it
   * can be mutated. This makes it easier going day to day because we can just
   * clone then change.
   */
  clone(start: Date): ParsedIterationRecordEventsTimeBucket {
    const clone = new ParsedIterationRecordEventsTimeBucket(start);
    clone.records = cloneDeep(this.records);
    clone.teamPerformance = this.teamPerformance.clone();
    Object.entries(this.teamUsers).forEach(([userId, user]) => {
      clone.teamUsers[userId] = user.clone();
    });
    clone.rollups = objectWithDefault<RollupData>(() => ({
      originalEstimate: 0,
      remainingEstimate: 0,
      workDone: 0,
      rollup: false,
      rolledUpFrom: new Set(),
    }));
    Object.entries(this.rollups).forEach(([recordId, rollup]) => {
      clone.rollups[recordId] = cloneDeep(rollup);
    });

    return clone;
  }
}

type TeamUserId = RecordId | 'unassigned';

export type ParsedRecordEvents = ParsedIterationRecordEventsTimeBucket[];

/**
 * Add to a number in an object that may be null or undefined. If it is null
 * then treat as zero before adding.
 */
export function addToNullableZero<
  T extends Partial<Record<K, number | null>>,
  K extends keyof T
>(object: T, key: K, addend: number): void {
  const currentValue = object[key] ?? 0;
  object[key] = (currentValue + addend) as T[K];
}

export interface ParsedIterationRecordEventsOptions {
  units?: WorkUnitEnum_value;
  splitBy?: moment.unitOfTime.Base;
}

export const PARSE_ITERATION_RECORD_EVENT_TYPES = [
  RecordEventTypeEnum.RECORD_ADDED_TO_ITERATION,
  RecordEventTypeEnum.RECORD_ADDED_TO_TEAM_WORKFLOW_STATUS,
  RecordEventTypeEnum.RECORD_COMPLETED,
  RecordEventTypeEnum.RECORD_ESTIMATE_CHANGED,
  RecordEventTypeEnum.RECORD_REMAINING_CHANGED,
  RecordEventTypeEnum.RECORD_WORK_DONE,
  RecordEventTypeEnum.RECORD_REMOVED_FROM_ITERATION,
  RecordEventTypeEnum.RECORD_RESTARTED,
  RecordEventTypeEnum.ITERATION_START,
  RecordEventTypeEnum.ITERATION_COMPLETED,
  RecordEventTypeEnum.RECORD_ITERATION_START,
  RecordEventTypeEnum.RECORD_ADDED_TO_ASSIGNED_USER,
  RecordEventTypeEnum.RECORD_REMOVED_FROM_ASSIGNED_USER,
];

/**
 * Go through the events and build up the data into a format that can be used by
 * reports. This returns data in time buckets. Each bucket has:
 *
 * - start: The start time of the bucket
 * - end: The end time of the bucket
 * - events: An array of events that happened in the bucket
 * - records: A map of recordId to ParsedRecordData holding info about each record
 * - teamPerformance: A Performance record for the team
 * - teamUsers: A map of userId to Performance
 * - rollups: A map of recordId to RollupData
 *
 * By default you'll get 1 bucket. If you pass in a splitBy option then you'll
 * get multiple buckets split by that time period.
 *
 * The bucket data is cumulative, so if a record is completed in bucket 1 and
 * another in bucket 2, the count in bucket 2 will be 2. Delta on performance
 * data can be achieved by calling toSubstracted.
 *
 * The counting starts when the record enters the iteration,
 * and ignores events without an iteration.
 *
 * @example
 *
 * ```typescript
 * const parsedEvents = parseIterationRecordEvents(events, {
 *  units: 'points',
 *  splitBy: 'day',
 * });
 * parsedEvents[0].records['123'].originalEstimate;
 * parsedEvents[1].teamPerformance.completed.count;
 *
 * const teamPerformanceDelta = parsedEvents[1]
 *   .teamPerformance
 *   .toSubstracted(parsedEvents[0].teamPerformance);
 * ```
 */
export function parseIterationRecordEvents(
  events: RecordEventRaw[],
  options: ParsedIterationRecordEventsOptions
): ParsedRecordEvents {
  const { units, splitBy } = options;
  const buckets: ParsedIterationRecordEventsTimeBucket[] = [];

  const records: { [recordId: string]: ParsedRecordData } = {};

  // If events happen at the same time (i.e. single transaction in the backend)
  // then they can be out of logical order. i.e. a record added to the assigned
  // user and then added to the iteration. We sort the events by time (which
  // they already are) and then by the order we expect them to happen in.
  const eventTypeOrder = [
    RecordEventTypeEnum.ITERATION_START,
    RecordEventTypeEnum.RECORD_ITERATION_START,
    RecordEventTypeEnum.RECORD_ADDED_TO_ITERATION,
    RecordEventTypeEnum.RECORD_ADDED_TO_TEAM_WORKFLOW_STATUS,
    RecordEventTypeEnum.RECORD_ADDED_TO_ASSIGNED_USER,
    RecordEventTypeEnum.RECORD_ESTIMATE_CHANGED,
    RecordEventTypeEnum.RECORD_REMAINING_CHANGED,
    RecordEventTypeEnum.RECORD_WORK_DONE,
    RecordEventTypeEnum.RECORD_COMPLETED,
    RecordEventTypeEnum.RECORD_RESTARTED,
    RecordEventTypeEnum.RECORD_REMOVED_FROM_ASSIGNED_USER,
    RecordEventTypeEnum.RECORD_REMOVED_FROM_TEAM_WORKFLOW_STATUS,
    RecordEventTypeEnum.RECORD_REMOVED_FROM_ITERATION,
  ].map(e => e.value) as RecordEventTypeEnum_value[];

  events.sort((a, b) => {
    const aTime = moment(a.createdAt).valueOf();
    const bTime = moment(b.createdAt).valueOf();
    if (aTime === bTime) {
      return (
        eventTypeOrder.indexOf(a.eventType) -
        eventTypeOrder.indexOf(b.eventType)
      );
    } else {
      return aTime - bTime;
    }
  });

  // We have an interesting problem with the remaining estimates. Because
  // changes to the remaining estimate is always correlated with another event,
  // estimate changed and work done, we cannot look at the remaining estimate
  // changed event and know what to do with it. But we cannot find that
  // correlated event very easily. So in order to work around this we keep track
  // of the remaining estimate as it changes for each record and use that when
  // looking at the original estimate and work done events.
  const recordRemainingEstimates: Record<RecordId, number[]> = {};

  const iterationEvents = {
    startEvent: events.find(
      ev => ev.eventType === RecordEventTypeEnum.ITERATION_START.value
    ),
    completedEvent: events.find(
      ev => ev.eventType === RecordEventTypeEnum.ITERATION_COMPLETED.value
    ),
  };

  const rollups = objectWithDefault<RollupData>(() => ({
    originalEstimate: 0,
    remainingEstimate: 0,
    workDone: 0,
    rollup: false,
    rolledUpFrom: new Set(),
  }));

  let bucket = new ParsedIterationRecordEventsTimeBucket(
    moment(iterationEvents.startEvent?.createdAt).toDate()
  );
  bucket.records = records;
  bucket.rollups = rollups;
  buckets.push(bucket);

  // Ignore sprints without a start event
  if (!iterationEvents.startEvent) {
    debug('No start event found for iteration', events);
    return buckets;
  }

  // Set the bucket start to be the start of the day of the iteration start or
  // the start of the splitBy period
  bucket.start = inCurrentUserTimezone(
    moment(iterationEvents.startEvent.createdAt)
  )
    .startOf(splitBy ?? 'day')
    .toDate();

  if (splitBy)
    bucket.end = inCurrentUserTimezone(moment(bucket.start))
      .endOf(splitBy)
      .toDate();

  const teamPerformance = bucket.teamPerformance;

  const interestingEvents = events.filter(
    event =>
      // Only interested in events attached to the iteration
      event.iteration &&
      // Not interested in events that have no record
      event.eventSubject?.id &&
      // Don't show Epics for sprint retros
      event.eventSubjectType !== 'Epic' &&
      // Ignore events that happened after the iteration finished
      (!iterationEvents.completedEvent ||
        moment(event.createdAt).valueOf() <
          moment(iterationEvents.completedEvent.createdAt).valueOf())
  );

  const rollupFeatures = new Set();
  interestingEvents.forEach(event => {
    if (
      event.eventSubject instanceof Requirement &&
      event.eventSubject.feature.showFeatureRemainingEstimate === false
    ) {
      rollupFeatures.add(event.eventSubject.feature.id);
      const rollup = rollups[event.eventSubject.feature.id];
      rollup.rollup = true;
      rollup.rolledUpFrom.add(event.eventSubject.id);
    }
  });

  // Build teamUsers object
  bucket.teamUsers.unassigned.user = new User({
    name: 'Unassigned',
    initials: 'N/A',
  });

  interestingEvents
    .map(event => event.assignedToUser)
    .forEach(user => {
      if (!isPresent(user?.id)) return;
      bucket.teamUsers[user.id] = new UserPerformance(user);
    });

  /**
   * For an event, count the points for the given key. If the event is in the
   * wrong unit type or is a rollup feature, then return 0.
   */
  const countEventPoints = (
    event: RecordEventRaw,
    key: 'originalEstimate' | 'remainingEstimate' | 'workDone'
  ) => {
    if (event.units === units && !rollupFeatures.has(event.eventSubject.id)) {
      return orZero(event[key]);
    } else {
      return 0;
    }
  };

  // Look for the record start events and use those to build the initial set of
  // records and set up the team users committed values.
  interestingEvents
    .filter(
      event =>
        event.eventType === RecordEventTypeEnum.RECORD_ITERATION_START.value
    )
    .forEach(event => {
      const completed = event.teamWorkflowStatus?.isCompleted ?? false;
      const started = event.teamWorkflowStatus?.isStarted ?? false;
      const originalEstimate = countEventPoints(event, 'originalEstimate');
      const remainingEstimate = countEventPoints(event, 'remainingEstimate');
      const workDone = countEventPoints(event, 'workDone');

      bucket.events.push(event);

      records[event.eventSubject.id] = {
        id: event.eventSubject.id,
        originalEstimate,
        remainingEstimate,
        workDone,
        committed: true,
        committedEstimate: remainingEstimate ?? originalEstimate,
        committedUserId: event.assignedToUser?.id,
        assignedToUser: event.assignedToUser,
        started,
        completed,
        removed: false,
        event,
        teamWorkflowStatus: event.teamWorkflowStatus,
        addedOn: moment(event.createdAt).format('YYYY-MM-DD'),
        referenceNum:
          ('referenceNum' in event.eventSubject &&
            event.eventSubject.referenceNum) ||
          '',
      };

      teamPerformance.addRecord(event);
      teamPerformance.committed.add({
        count: 1,
        originalEstimate,
        remainingEstimate,
      });

      const user = bucket.teamUsers[event.assignedToUser?.id ?? 'unassigned'];
      user.assignRecord(event);
      user.committed.add({
        count: 1,
        originalEstimate,
        remainingEstimate,
      });

      if (completed) {
        [teamPerformance, user].forEach(performance => {
          performance.completed.add({
            count: 1,
            originalEstimate,
            remainingEstimate,
          });
        });
      } else {
        [teamPerformance, user].forEach(performance => {
          performance.incomplete.add({
            count: 1,
            originalEstimate,
            remainingEstimate,
          });
        });
      }

      if (event.units === units) {
        recordRemainingEstimates[event.eventSubject.id] = [remainingEstimate];
      }

      const rollup =
        'feature' in event.eventSubject &&
        rollups[event.eventSubject?.feature?.id].rollup
          ? rollups[event.eventSubject?.feature?.id]
          : null;
      if (rollup) {
        rollup.originalEstimate += originalEstimate;
        rollup.remainingEstimate += remainingEstimate;
      }
    });

  // Now we're going to loop over all the events, ignore anything before the
  // start event, and build up the records and team users effort values
  for (const event of interestingEvents) {
    debug(event.eventType, event);

    // Ignore the start events we've already dealt with
    if (event.eventType === RecordEventTypeEnum.RECORD_ITERATION_START.value)
      continue;

    // Only consider events on feaatures/epics/requirements
    if (event.eventSubjectType === 'Iteration') continue;

    const createdAt = moment(event.createdAt);

    const afterStart =
      createdAt.valueOf() >
      moment(iterationEvents.startEvent.createdAt).valueOf();

    if (!afterStart) continue;

    if (splitBy) {
      // Create buckets until we reach the one the event will go in. There might
      // be a gap in the events, i.e. you'd expect buckets for day 1,2,3 but we
      // only have events for 1 and 3. This will create the missing bucket for
      // day 2 by cloning day 1.
      while (createdAt.valueOf() > bucket.end.valueOf()) {
        const nextBucketStart = moment(bucket.start).add(1, splitBy);
        const nextBucket = bucket.clone(nextBucketStart.toDate());
        nextBucket.end = inCurrentUserTimezone(nextBucketStart)
          .endOf(splitBy)
          .toDate();
        buckets.push(nextBucket);

        bucket = nextBucket;

        if (createdAt.valueOf() <= bucket.end.valueOf()) {
          break;
        }
      }
    }
    bucket.events.push(event);

    const { records, rollups, teamPerformance, teamUsers } = bucket;

    // If requirements roll up to a feature, we don't show the estimate of the feature, but instead we
    // need to accumulate the estimates of the requirements
    const rollup =
      'feature' in event.eventSubject &&
      rollups[event.eventSubject?.feature?.id].rollup
        ? rollups[event.eventSubject?.feature?.id]
        : null;
    const started = event.teamWorkflowStatus?.isStarted ?? false;
    const completed = event.teamWorkflowStatus?.isCompleted ?? false;
    const assignmentChange =
      event.assignedToUser?.id !==
      records[event.eventSubject.id]?.assignedToUser?.id;

    // Add the record if it isn't already in the records object
    const recordAlreadySeen = !!records[event.eventSubject.id];

    if (!records[event.eventSubject.id]) {
      records[event.eventSubject.id] = {
        id: event.eventSubject.id,
        event,
        started,
        completed,
        committed: false,
        assignedToUser: event.assignedToUser,
        referenceNum:
          ('referenceNum' in event.eventSubject &&
            event.eventSubject.referenceNum) ||
          '',
      };
    }

    // If the event has a different unit to the iteration then ignore the point value
    const originalEstimate = countEventPoints(event, 'originalEstimate');
    const remainingEstimate = countEventPoints(event, 'remainingEstimate');
    const workDone = countEventPoints(event, 'workDone');

    // Add the remaining estimate array if it isn't already in the
    // recordRemainingEstimates object
    if (
      event.units === units &&
      !recordRemainingEstimates[event.eventSubject.id]
    ) {
      recordRemainingEstimates[event.eventSubject.id] = [remainingEstimate];
    }

    // Extract the remaining estimate values to see if that is changing
    const remainingEstimates = recordRemainingEstimates[event.eventSubject.id];
    const lastRemainingEstimate = last(remainingEstimates);
    const remainingEstimateDelta =
      event.units === units
        ? remainingEstimate - (lastRemainingEstimate ?? 0)
        : 0;

    const record = records[event.eventSubject.id];
    const user = teamUsers[event.assignedToUser?.id ?? 'unassigned'];

    switch (event.eventType) {
      // When the record is added to the iteration we do much the same as if the
      // record was there on iteration start, except we don't add it to the
      // committed effort
      case RecordEventTypeEnum.RECORD_ADDED_TO_ITERATION.value:
        merge<typeof record, Partial<typeof record>>(record, {
          originalEstimate,
          remainingEstimate,
          workDone,
          removed: false,
          teamWorkflowStatus: event.teamWorkflowStatus,
          addedOutsideIteration:
            records[event.eventSubject.id].addedOutsideIteration ??
            (event.teamWorkflowStatus && started),
          addedOn:
            records[event.eventSubject.id].addedOn ??
            moment(event.createdAt).format('YYYY-MM-DD'),
        });

        teamPerformance.addRecord(event);
        user.assignRecord(event);

        if (event.assignedToUser) {
          record.assignedToUser = event.assignedToUser;
        }

        if (completed) {
          // We only add to completed if the record wasn't already in the
          // records. This is to prevent doubling up if you remove then add back
          // a record
          if (recordAlreadySeen) {
            debug('Record already seen', record);
          } else {
            [teamPerformance, user].forEach(performance => {
              performance.added.add({
                count: 1,
                originalEstimate,
                remainingEstimate,
              });
              performance.completed.add({
                count: 1,
                originalEstimate,
                remainingEstimate,
              });
            });
          }
        } else if (!completed) {
          [teamPerformance, user].forEach(performance => {
            performance.added.add({
              count: 1,
              originalEstimate,
              remainingEstimate,
            });
            // We always add to incomplete if its not complete because when it's
            // removed from an iteration we always subtract from incomplete
            performance.incomplete.add({
              count: 1,
              originalEstimate,
              remainingEstimate,
            });
          });
        }

        if (rollup) {
          rollup.originalEstimate += originalEstimate;
          rollup.remainingEstimate += remainingEstimate;
        }
        break;
      // When removing from the iteration we mark that as removed work, and we
      // subtract it from incomplete. We don't touch complete. The assumption is
      // that if you completed it in the iteration then it should remain counted
      // as completed.
      case RecordEventTypeEnum.RECORD_REMOVED_FROM_ITERATION.value:
        record.removed = true;
        record.removedOn = moment(event.createdAt).format('YYYY-MM-DD');
        if (!completed) {
          [teamPerformance, user].forEach(performance => {
            performance.removed.add({
              count: 1,
              originalEstimate,
              remainingEstimate,
            });
            performance.incomplete.subtract({
              count: 1,
              originalEstimate,
              remainingEstimate,
            });
          });
        }
        break;
      // When the record is completed it goes to the completed effort and is
      // removed from the incomplete effort
      case RecordEventTypeEnum.RECORD_COMPLETED.value:
        if (record.completed) {
          debug('Record already completed', record);
          break;
        }
        record.completed = true;
        if (event.units === units) remainingEstimates.push(remainingEstimate);

        [teamPerformance, user].forEach(performance => {
          performance.completed.add({
            count: 1,
            originalEstimate,
            remainingEstimate: Math.abs(remainingEstimateDelta),
          });
          performance.incomplete.subtract({
            count: 1,
            originalEstimate,
            remainingEstimate: Math.abs(remainingEstimateDelta),
          });
        });
        break;
      // When the record is restarted it reverses the completed and incomplete.
      // Note that we don't change the remaining effort as we'll get separate
      // events if that changes
      case RecordEventTypeEnum.RECORD_RESTARTED.value:
        record.completed = false;
        [teamPerformance, user].forEach(performance => {
          performance.completed.subtract({
            count: 1,
            originalEstimate,
          });
          performance.incomplete.add({
            count: 1,
            originalEstimate,
          });
        });

        break;
      // If the record estimate is updated then we see if its going up or down.
      // If its going up then is is work added, if its going down then its work
      // removed. We also update the incomplete effort with changes to the
      // remaining estimate
      case RecordEventTypeEnum.RECORD_ESTIMATE_CHANGED.value:
        addToNullableZero(record, 'originalEstimate', originalEstimate);
        if (event.units === units) remainingEstimates.push(remainingEstimate);

        if (originalEstimate > 0) {
          [teamPerformance, user].forEach(performance => {
            performance.added.add({
              originalEstimate,
              remainingEstimate: Math.abs(remainingEstimateDelta),
            });
            performance.incomplete.add({
              remainingEstimate: Math.abs(remainingEstimateDelta),
            });
          });
        } else {
          [teamPerformance, user].forEach(performance => {
            performance.removed.add({
              originalEstimate: Math.abs(originalEstimate),
              remainingEstimate: Math.abs(remainingEstimateDelta),
            });
            performance.incomplete.subtract({
              remainingEstimate: Math.abs(remainingEstimateDelta),
            });
          });
        }

        if (completed) {
          [teamPerformance, user].forEach(performance => {
            performance.completed.add({ originalEstimate });
          });
        } else {
          [teamPerformance, user].forEach(performance => {
            performance.incomplete.add({ originalEstimate });
          });
        }

        if (rollup) rollup.originalEstimate += originalEstimate;
        break;
      // If the remaining estimate is updated then we update the record but we
      // do not update the efforts because the remaining estimate change is
      // always correlated with the original estimate change or work done
      case RecordEventTypeEnum.RECORD_REMAINING_CHANGED.value:
        addToNullableZero(record, 'remainingEstimate', remainingEstimate);
        if (rollup) rollup.remainingEstimate += remainingEstimate;
        break;
      // work done means adding to completed and removing from incomplete
      case RecordEventTypeEnum.RECORD_WORK_DONE.value:
        addToNullableZero(record, 'workDone', workDone);

        // Note that we do not use the work done number for the user effort
        // buckets here. Instead we take a delta of the remaining estimate. The
        // reason for this is that a user can set work done and remaining
        // independently, but it's the remaining work left on the record that
        // counts at any given time. This could potentially cause customer
        // confusion, but it's a rare case they would independently count work
        // done and remaining.
        if (event.units === units) remainingEstimates.push(remainingEstimate);

        [teamPerformance, user].forEach(performance => {
          performance.completed.add({
            remainingEstimate: Math.abs(remainingEstimateDelta),
            workDone,
          });
          performance.incomplete.subtract({
            remainingEstimate: Math.abs(remainingEstimateDelta),
          });
        });

        if (rollup) rollup.workDone += workDone;
        break;
      case RecordEventTypeEnum.RECORD_ADDED_TO_TEAM_WORKFLOW_STATUS.value:
        record.teamWorkflowStatus = event.teamWorkflowStatus;

        if (started && !record.started) {
          record.started = true;
        }

        break;
      // When assignment changes mark it as added effort for the user (i.e. not
      // committed)
      case RecordEventTypeEnum.RECORD_ADDED_TO_ASSIGNED_USER.value:
        if (!assignmentChange) break;

        record.assignedToUser = event.assignedToUser;

        if (completed) {
          if (user.recordAlreadySeen(event)) {
            debug('Record already seen', record);
          } else {
            user.added.add({ count: 1, originalEstimate });
            user.completed.add({ count: 1, originalEstimate });
          }
        } else {
          user.added.add({ count: 1, originalEstimate, remainingEstimate });
          user.incomplete.add({
            count: 1,
            originalEstimate,
            remainingEstimate,
          });
        }

        user.assignRecord(event);
        break;
      // When unassigning we mark that as removed work effort, and subtract from
      // the incomplete effort
      case RecordEventTypeEnum.RECORD_REMOVED_FROM_ASSIGNED_USER.value:
        record.assignedToUser = undefined;

        if (completed) {
          debug('Completed record does not get removed from user', record);
        } else {
          user.removed.add({ count: 1, originalEstimate, remainingEstimate });
          user.incomplete.subtract({
            count: 1,
            originalEstimate,
            remainingEstimate,
          });
        }

        break;
    }
  }

  const lastEvent = last(bucket.events);
  if (lastEvent) bucket.end = moment(lastEvent.createdAt).toDate();

  return buckets;
}
